import Alert, { type AlertProps } from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button, { type ButtonProps } from "@mui/material/Button";
import Container from "@mui/material/Container";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { X as CloseIcon } from "@phosphor-icons/react/X";
import * as React from "react";

export interface BannerAction {
	href: string;
	label: string;
	variant?: ButtonProps["variant"];
}

export interface BannerConfig {
	actions?: BannerAction[];
	color?: AlertProps["color"];
	enabled: boolean;
	id: string;
	message: string;
	variant?: AlertProps["variant"];
}

interface AnnouncementBannerProps {
	config: BannerConfig;
}

function getDismissedBannerStorageKey(id: string) {
	return `banner-dismissed:${id}`;
}

export function AnnouncementBanner({
	config,
}: AnnouncementBannerProps): React.JSX.Element | null {
	const [isDismissed, setIsDismissed] = React.useState(false);

	React.useEffect(() => {
		if (typeof window === "undefined") {
			return;
		}

		try {
			setIsDismissed(
				window.sessionStorage.getItem(
					getDismissedBannerStorageKey(config.id),
				) === "true",
			);
		} catch {
			setIsDismissed(false);
		}
	}, [config.id]);

	if (!config.enabled || isDismissed) {
		return null;
	}

	const handleDismiss = () => {
		setIsDismissed(true);

		if (typeof window === "undefined") {
			return;
		}

		try {
			window.sessionStorage.setItem(
				getDismissedBannerStorageKey(config.id),
				"true",
			);
		} catch {
			// Ignore sessionStorage failures and fall back to in-memory dismissal.
		}
	};

	return (
		<Alert
			color={config.color}
			icon={false}
			variant={config.variant ?? "filled"}
			sx={{
				backgroundColor:
					config.variant === "outlined"
						? "var(--mui-palette-common-white)"
						: undefined,
				borderRadius: 0,
				py: 0.75,
				"& .MuiAlert-message": {
					py: 0,
					width: "100%",
				},
			}}>
			<Container maxWidth="lg">
				<Stack
					direction="row"
					spacing={1.5}
					sx={{
						alignItems: "flex-start",
						justifyContent: "space-between",
						py: 0.5,
					}}>
					<Stack
						spacing={1.5}
						sx={{
							flex: "1 1 auto",
							minWidth: 0,
						}}>
						<Stack
							direction={{ xs: "column", md: "row" }}
							spacing={1.5}
							sx={{
								alignItems: { md: "center" },
								justifyContent: "space-between",
							}}>
							<Typography
								sx={{
									fontSize: "0.9375rem",
									fontWeight: 500,
									lineHeight: 1.5,
								}}
								variant="body2">
								{config.message}
							</Typography>
							{config.actions?.length ? (
								<Stack
									direction="row"
									spacing={1}
									sx={{
										alignItems: "center",
										flexShrink: 0,
										flexWrap: "wrap",
										rowGap: 1,
									}}>
									{config.actions.map((action) => (
										<Button
											key={action.href}
											color="inherit"
											component="a"
											href={action.href}
											rel="noreferrer"
											size="small"
											target="_blank"
											variant={action.variant ?? "outlined"}>
											{action.label}
										</Button>
									))}
								</Stack>
							) : null}
						</Stack>
					</Stack>
					<Box sx={{ flexShrink: 0 }}>
						<IconButton
							aria-label="Dismiss announcement"
							color="inherit"
							onClick={handleDismiss}
							size="small">
							<CloseIcon size={18} />
						</IconButton>
					</Box>
				</Stack>
			</Container>
		</Alert>
	);
}
